import { Component, forwardRef, SimpleChanges, ViewChild, inject, AfterContentInit, DestroyRef, input, output, Input, signal } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslocoService } from '@ngneat/transloco';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, filter, fromEvent, tap, Subject } from 'rxjs';
import { ContentChange, QuillEditorComponent } from 'ngx-quill';
import Quill, { Delta } from 'quill';
import Block from 'quill/blots/block';
import { Mention, MentionBlot, MentionOption } from 'quill-mention';
import { SvcControl } from '../svc-control';

Quill.debug('error');
Quill.register({ 'blots/mention': MentionBlot, 'modules/mention': Mention });
Block.tagName = 'DIV';
Quill.register(Block, true);

export type SvcQuillTextEditorMentionEvent<T extends SvcQuillTextEditorMention> = {
  searchTerm: string;
  mentionChar: string;
  setList: (list: T[]) => void;
};

export type SvcQuillTextEditorMention = {
  id?: string | number;
  value?: string;
  denotationChar?: string;
};

@Component({
  selector: 'svc-quill-text-editor',
  templateUrl: './svc-quill-text-editor.component.html',
  styleUrls: ['./svc-quill-text-editor.component.scss'],
  host: {
    '[class.svc-quill-text-editor]': `true`,
  },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SvcQuillTextEditorComponent),
      multi: true,
    },
  ],
})
export class SvcQuillTextEditorComponent extends SvcControl implements AfterContentInit {
  inputId = input<string>();
  readOnly = input<boolean>(false);
  placeholder = input<string>();
  height = input<string>();
  minHeight = input<string>();
  maxHeight = input<string>();
  applyBorder = input<boolean>(true);
  applyTheme = input<boolean>(false);
  applyToolbar = input<boolean>(false);
  applyMention = input<boolean>(false);
  showValidationMessage = input<boolean>(true);
  @Input() allowMentionAt = signal(true);
  @Input() allowMentionHashtag = signal(true);

  onRender = output<SvcQuillTextEditorComponent>();
  ngModelChange = output<string>();
  onContentChanged = output<ContentChange>();
  onMentionSelected = output<unknown>();
  onMentionTriggered = output<SvcQuillTextEditorMentionEvent<never>>();

  @ViewChild('editor') editor: QuillEditorComponent;

  #transloco = inject(TranslocoService);
  #destroyRef = inject(DestroyRef);
  #onMentionTermChanged = new Subject<SvcQuillTextEditorMentionEvent<never>>();

  get text(): string {
    return this.editorInstance.getText();
  }

  get html(): string {
    return this.editorInstance.root.innerHTML;
  }
  set html(html) {
    this.editorInstance.root.innerHTML = html;
  }

  get deltas(): Delta {
    return this.editorInstance.getContents();
  }

  set deltas(deltas: Delta) {
    this.editorInstance.setContents(deltas, 'silent');
  }

  public editorInstance: Quill;
  public internalClasses: string;

  get quillEditor(): QuillEditorComponent {
    return this.editor;
  }
  protected mentionList: SvcQuillTextEditorMention[] = [];
  protected styles = {};
  protected modulesMention: Partial<MentionOption> = {
    minChars: 0,
    maxChars: 31,
    dataAttributes: ['id', 'value', 'denotationChar'],
    positioningStrategy: 'fixed',
    defaultMenuOrientation: 'bottom',
    allowedChars: /(^[A-zÀ-ÿ0-9]+)/,
    mentionDenotationChars: ['@', '#'],
    isolateCharacter: true,
    mentionListClass: 'mention-editor-list',
    onSelect: (item: SvcQuillTextEditorMention, insertItem: (mention: SvcQuillTextEditorMention) => void): void => {
      const mention = this.mentionList.find((m) => m.id && m.id.toString() === item.id.toString());
      if (mention) {
        mention.denotationChar = item.denotationChar;
        this.onMentionSelected.emit(mention);
        insertItem(mention);
      }
    },
    renderLoading: (): string => {
      if (!this.allowMentionAt() && !this.allowMentionHashtag()) return null;
      return this.#transloco.translate('Digite ao menos 3 caracteres');
    },
    source: (searchTerm: string, renderList: (list: never[], term: string) => void, mentionChar: string): void => {
      if (!this.allowMentionAt() && !this.allowMentionHashtag()) return null;
      this.#onMentionTermChanged.next({
        setList: (mentionList) => {
          this.mentionList = mentionList;
          renderList(mentionList, searchTerm);
        },
        searchTerm,
        mentionChar,
      });
    },
  };
  protected modulesToolbar = [
    ['bold', 'italic', 'underline', 'strike'], // toggled buttons
    ['blockquote', 'code-block'],
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ script: 'sub' }, { script: 'super' }], // superscript/subscript
    [{ indent: '-1' }, { indent: '+1' }], // outdent/indent
    [{ direction: 'rtl' }], // text direction
    [{ color: [] }, { background: [] }], // dropdown with defaults from theme
    [{ font: [] }],
    [{ align: [] }],
    ['clean'], // remove formatting button
    ['link'], // link and image, video
  ];

  ngOnInit(): void {
    super.ngOnInit();
    this.#onMentionTermChanged
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        debounceTime(500),
        tap((event) => this.onMentionTriggered.emit(event))
      )
      .subscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);
    if ('height' in changes || 'minHeight' in changes || 'maxHeight' in changes || 'applyBorder' in changes) {
      const styles: { [key: string]: string } = {};
      if (!this.applyBorder()) {
        styles.border = '0';
        styles.boxShadow = 'unset';
      }
      if (this.height()) styles.height = this.height();
      if (this.minHeight()) styles.minHeight = this.minHeight();
      if (this.maxHeight()) styles.maxHeight = this.maxHeight();
      this.styles = styles;
    }
    if ('applyMention' in changes) {
      let classes = this.internalClasses?.split(' ') ?? [];
      if (this.applyMention) classes = Array.from(new Set([...classes, 'mentions-container']));
      else classes = classes.filter((c) => c === 'mentions-container');
      this.internalClasses = classes.join(' ');
    }
    if ('allowMentionAt' in changes || 'allowMentionHashtag' in changes) {
      this.checkMentionCharacters();
    }
  }

  public ngAfterContentInit(): void {
    if (!this.editorInstance) return;
    this.onContentChanged.emit({
      editor: this.editorInstance,
      content: this.editorInstance.getContents(),
      delta: this.editorInstance?.editor.delta,
      html: this.editorInstance?.root.innerHTML,
      oldDelta: this.editorInstance?.editor.delta,
      source: '',
      text: this.editorInstance?.getText(),
    });
  }

  onChange(newVal: string): void {
    super.onChange(newVal);
    this.ngModelChange.emit(newVal);
  }

  public focus(): void {
    this.editorInstance?.focus();
  }

  public insertMention<T extends SvcQuillTextEditorMention>(mention: T): void {
    const module = this.editorInstance?.getModule('mention') as {
      insertItem: (data: SvcQuillTextEditorMention, programmaticInsert?: boolean, overriddenOptions?: { blotName: string }) => void;
      mentionCharPos?: number;
    };
    if (module && module.mentionCharPos == null) {
      module.mentionCharPos = 0;
    }
    module?.insertItem(mention, true);
  }

  public insertMentions<T extends SvcQuillTextEditorMention>(mentions: T[]): void {
    for (const mention of mentions) {
      this.insertMention(mention);
    }
  }

  public extractMentions<T extends SvcQuillTextEditorMention>(): T[] {
    return this.deltas.filter((d) => typeof d.insert === 'object' && 'mention' in d.insert).map((d) => d.insert?.['mention']) ?? [];
  }

  public checkMentionCharacters(): void {
    const chars: string[] = [];
    if (this.allowMentionAt()) chars.push('@');
    if (this.allowMentionHashtag()) chars.push('#');
    this.modulesMention = {
      ...this.modulesMention,
      mentionDenotationChars: chars,
    };
  }

  protected onEditorCreated(editor: Quill): void {
    this.editorInstance = editor;
    this.#startListenPaste();
    this.#startListenEditorElementClick();
    this.ngAfterContentInit();
    this.onRender.emit(this);
  }

  protected onContentChangedListen(event: ContentChange): void {
    this.onContentChanged.emit(event);
  }

  protected filterValue(value: string): string {
    return value?.replace(/&nbsp;/g, ' ').trim();
  }

  #startListenEditorElementClick(): void {
    const qlEditor = this.editorInstance.root.parentElement;
    if (qlEditor) {
      fromEvent(qlEditor, 'click')
        .pipe(
          takeUntilDestroyed(this.#destroyRef),
          filter(() => !this.readOnly()),
          tap((event: Event) => {
            const target = event.target as HTMLElement;
            if (target.classList?.contains('ql-container')) {
              this.focus();
            }
          })
        )
        .subscribe();
    }
  }

  #startListenPaste(): void {
    this.editorInstance.clipboard.addMatcher(Node.ELEMENT_NODE, (node: HTMLElement) => {
      const plainText = node.innerText || '';
      return new Delta().insert(plainText);
    });
  }
}
